import React from 'react';
import { ShotVersion } from '../../../types';
import { tosAccelerate } from '../../../utils/tosAccelerate';
import { cssAspectRatio } from '../utils/cssAspectRatio';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const VIDEO_BOX_MAX_HEIGHT = '300px';

interface VideoLoadPlaceholderProps {
  aspectRatio?: string;
  label: string;
  onActivate: () => void;
}

/**
 * Click-to-load stand-in for a version video. Keeps hundreds of
 * <video> elements out of the DOM until the user actually asks for one.
 */
export function VideoLoadPlaceholder({ aspectRatio, label, onActivate }: VideoLoadPlaceholderProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(event) => { event.stopPropagation(); onActivate(); }}
      style={{
        width: '100%',
        aspectRatio: cssAspectRatio(aspectRatio),
        maxHeight: VIDEO_BOX_MAX_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#000',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.18)',
          border: '2px solid rgba(255,255,255,0.7)',
          color: '#fff',
          fontSize: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingLeft: '4px',
          lineHeight: 1,
        }}
      >
        ▶
      </span>
    </button>
  );
}

interface ShotVersionPreviewProps {
  version: ShotVersion;
  isPrimary: boolean;
  isSecondary: boolean;
  showVersionActions: boolean;
  played: boolean;
  cancelFailed: boolean;
  canceling: boolean;
  /** Whether the user has activated (click-to-load) this version's video. */
  active: boolean;
  /** Project aspect ratio ("9:16" / "16:9" / "21:9") for the placeholder box. */
  aspectRatio?: string;
  t: Translate;
  onActivate: () => void;
  onPlayed: () => void;
  onToggleSecondary: () => void;
  onSelectPrimary: () => void;
  onCancel: () => void;
}

export default function ShotVersionPreview({
  version,
  isPrimary,
  isSecondary,
  showVersionActions,
  played,
  cancelFailed,
  canceling,
  active,
  aspectRatio,
  t,
  onActivate,
  onPlayed,
  onToggleSecondary,
  onSelectPrimary,
  onCancel,
}: ShotVersionPreviewProps) {
  const boxRatio = cssAspectRatio(aspectRatio);

  if (version.taskStatus === 'completed' && version.videoURL) {
    const handleToggleSecondary = (event: React.MouseEvent) => {
      event.stopPropagation();
      onToggleSecondary();
    };
    const handleSelectPrimary = (event: React.MouseEvent) => {
      event.stopPropagation();
      onSelectPrimary();
    };

    return (
      <div style={{ position: 'relative' }}>
        {active ? (
          <video
            src={tosAccelerate(version.videoURL)}
            controls
            preload="metadata"
            autoPlay
            style={{ width: '100%', aspectRatio: boxRatio, maxHeight: VIDEO_BOX_MAX_HEIGHT, objectFit: 'contain', display: 'block', background: '#000' }}
            onPlay={onPlayed}
          />
        ) : (
          <VideoLoadPlaceholder aspectRatio={aspectRatio} label={t('version.loadVideo')} onActivate={onActivate} />
        )}
        {!played && (
          <span style={{ position: 'absolute', top: '6px', left: '6px', background: '#22c55e', color: '#fff', fontSize: '0.6rem', fontWeight: 700, padding: '2px 5px', borderRadius: '3px', lineHeight: 1, zIndex: 3 }}>NEW</span>
        )}
        {showVersionActions && !isPrimary && (
          <button
            onClick={handleToggleSecondary}
            style={{
              position: 'absolute', top: '6px', right: '34px',
              background: isSecondary ? 'var(--theme-storyboard)' : 'rgba(0,0,0,0.5)',
              color: '#fff', border: isSecondary ? '2px solid #fff' : '2px solid rgba(255,255,255,0.6)',
              borderRadius: '50%', width: '24px', height: '24px',
              cursor: 'pointer', fontSize: '0.7rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
              lineHeight: 1,
            }}
            title={isSecondary ? t('version.removeSecondary') : t('version.secondaryTitle')}
          >
            {isSecondary ? '★' : '☆'}
          </button>
        )}
        {showVersionActions && (
          <button
            onClick={handleSelectPrimary}
            style={{
              position: 'absolute', top: '6px', right: '6px',
              background: isPrimary ? 'var(--theme-storyboard)' : 'rgba(0,0,0,0.5)',
              color: '#fff', border: isPrimary ? '2px solid #fff' : '2px solid rgba(255,255,255,0.6)',
              borderRadius: '50%', width: '24px', height: '24px',
              cursor: 'pointer', fontSize: '0.7rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
            }}
            title={isPrimary ? t('version.isPrimary') : t('version.markPrimary')}
          >
            ✓
          </button>
        )}
      </div>
    );
  }

  if (version.taskStatus === 'generating') {
    return (
      <div style={{ width: '100%', aspectRatio: boxRatio, maxHeight: VIDEO_BOX_MAX_HEIGHT, position: 'relative', background: 'var(--surface-2)' }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
          <span className="loading-spinner" style={{ width: '24px', height: '24px', borderWidth: '3px' }}></span>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{t('version.generating')}</span>
          {version.videoPlatform === 'modelco' ? null : cancelFailed ? (
            <span style={{ fontSize: '0.72rem', color: 'var(--text-3)' }}>{t('version.cannotCancel')}</span>
          ) : (
            <button
              onClick={(event) => { event.stopPropagation(); onCancel(); }}
              disabled={canceling}
              style={{ marginTop: '0.25rem', fontSize: '0.75rem', padding: '3px 12px', background: 'rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.15)', borderRadius: '4px', cursor: 'pointer', color: 'var(--text-2)' }}
            >
              {canceling ? <><span className="loading-spinner" style={{ width: '10px', height: '10px', borderWidth: '2px' }}></span> {t('version.canceling')}</> : t('version.cancelGeneration')}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (version.taskStatus === 'cancelled') {
    return (
      <div style={{ width: '100%', aspectRatio: boxRatio, maxHeight: VIDEO_BOX_MAX_HEIGHT, position: 'relative', background: 'var(--danger-light)' }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
          <span style={{ fontSize: '1.5rem' }}>🚫</span>
          <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>{t('version.userCancelled')}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', aspectRatio: boxRatio, maxHeight: VIDEO_BOX_MAX_HEIGHT, position: 'relative', background: 'var(--danger-light)' }}>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
        <span style={{ fontSize: '1.5rem' }}>❌</span>
        <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>{t('version.generationFailed')}</span>
      </div>
    </div>
  );
}
